// =============================================================================
// PlansPage — Admin plan management (full CRUD + lifecycle)
// =============================================================================
// Supports:
//   • Create new plans with explicit slug + provider scope (Stripe Price id
//     and/or Shopify Managed Pricing plan handle).
//   • Edit plan name, prices (decimal mirrors cents), discount, limits,
//     features, billing handles, and active toggle.
//   • Lifecycle transitions through dedicated buttons:
//       - Deprecate / Undeprecate — hide from new-signup catalog while
//         keeping existing subscriptions on the plan.
//       - Archive / Restore — soft-delete a plan (blocked while any
//         org or open subscription references it).
//   • Filter: include archived plans on demand.
// =============================================================================

import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
  type ReactNode,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Input,
  Modal,
  ModalFooter,
  Spinner,
} from '@tkpod/ui';

import { api } from '@/lib/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ProviderScope = 'INTERNAL' | 'STRIPE_ONLY' | 'SHOPIFY_ONLY' | 'BOTH';

interface Plan {
  id: string;
  name: string;
  slug: string;
  monthlyPrice: string;
  yearlyPrice: string | null;
  monthlyPriceUsdCents: number;
  yearlyPriceUsdCents: number | null;
  trialDays: number;
  stripePriceId: string | null;
  shopifyAppPlanHandle: string | null;
  discountPercentage: string;
  storageLimit: number;
  exportsLimit: number;
  storesLimit: number;
  usersLimit: number;
  features: string[];
  isActive: boolean;
  archivedAt: string | null;
  deprecatedAt: string | null;
  sortOrder: number;
  providerScope: ProviderScope;
  organizationCount: number;
  activeSubscriptionCount: number;
}

interface PlansResponse {
  data: Plan[];
}

interface PlanResponse {
  data: Plan;
}

interface PlanFormState {
  name: string;
  slug: string;
  monthlyPriceUsdCents: number;
  yearlyPriceUsdCents: number | null;
  trialDays: number;
  stripePriceId: string;
  shopifyAppPlanHandle: string;
  discountPercentage: string;
  storageLimit: number;
  exportsLimit: number;
  storesLimit: number;
  usersLimit: number;
  sortOrder: number;
  features: string[];
  isActive: boolean;
}

type ModalMode = 'create' | 'edit' | null;

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const AVAILABLE_FEATURES = [
  { value: 'basic_designer', label: 'Basic Designer' },
  { value: 'dtf_by_size', label: 'DTF by Size' },
  { value: 'gangsheet', label: 'Gang Sheet' },
  { value: 'uv_dtf', label: 'UV DTF' },
  { value: 'advanced_designer', label: 'Advanced Designer' },
  { value: 'api_access', label: 'API Access' },
  { value: 'custom_branding', label: 'Custom Branding' },
  { value: 'priority_support', label: 'Priority Support' },
];

const EMPTY_FORM: PlanFormState = {
  name: '',
  slug: '',
  monthlyPriceUsdCents: 0,
  yearlyPriceUsdCents: null,
  trialDays: 0,
  stripePriceId: '',
  shopifyAppPlanHandle: '',
  discountPercentage: '0',
  storageLimit: 0,
  exportsLimit: 0,
  storesLimit: 0,
  usersLimit: 0,
  sortOrder: 0,
  features: [],
  isActive: true,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function centsToDecimal(cents: number | null | undefined): string {
  if (cents == null) return '—';
  if (cents === 0) return 'Free';
  return `$${(cents / 100).toFixed(2)}`;
}

function formatLimit(limit: number): string {
  return limit === -1 ? 'Unlimited' : String(limit);
}

function parseCents(input: string): number {
  const trimmed = input.trim();
  if (trimmed === '' || trimmed === '0') return 0;
  // Accept both "19" and "19.00"
  const num = Number(trimmed);
  if (Number.isNaN(num) || num < 0) return 0;
  return Math.round(num * 100);
}

function planToForm(plan: Plan): PlanFormState {
  return {
    name: plan.name,
    slug: plan.slug,
    monthlyPriceUsdCents: plan.monthlyPriceUsdCents,
    yearlyPriceUsdCents: plan.yearlyPriceUsdCents,
    trialDays: plan.trialDays,
    stripePriceId: plan.stripePriceId ?? '',
    shopifyAppPlanHandle: plan.shopifyAppPlanHandle ?? '',
    discountPercentage: plan.discountPercentage,
    storageLimit: plan.storageLimit,
    exportsLimit: plan.exportsLimit,
    storesLimit: plan.storesLimit,
    usersLimit: plan.usersLimit,
    sortOrder: plan.sortOrder,
    features: [...plan.features],
    isActive: plan.isActive,
  };
}

function deriveProviderScope(form: PlanFormState): ProviderScope {
  if (form.slug === 'free') return 'INTERNAL';
  const stripe = form.stripePriceId.trim().length > 0;
  const shopify = form.shopifyAppPlanHandle.trim().length > 0;
  if (stripe && shopify) return 'BOTH';
  if (stripe) return 'STRIPE_ONLY';
  if (shopify) return 'SHOPIFY_ONLY';
  return 'INTERNAL';
}

function describeProviderScope(scope: ProviderScope): string {
  switch (scope) {
    case 'BOTH':
      return 'Available to both Stripe-billed and Shopify-billed merchants.';
    case 'STRIPE_ONLY':
      return 'Stripe-billed merchants only. Shopify-connected stores will NOT see this plan.';
    case 'SHOPIFY_ONLY':
      return 'Shopify Managed Pricing only. Stripe-billed merchants will NOT see this plan. The handle must match the plan name configured in your Partner Dashboard.';
    case 'INTERNAL':
      return 'Free / internal plan — no external billing handles required.';
  }
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const queryKeys = {
  plans: (includeArchived: boolean) =>
    ['admin', 'plans', { includeArchived }] as const,
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PlansPage() {
  const queryClient = useQueryClient();

  const [showArchived, setShowArchived] = useState(false);
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [editTarget, setEditTarget] = useState<Plan | null>(null);
  const [form, setForm] = useState<PlanFormState>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);

  const plansQuery = useQuery<PlansResponse, Error>({
    queryKey: queryKeys.plans(showArchived),
    queryFn: () =>
      api.get<PlansResponse>(
        `/api/v1/admin/plans${showArchived ? '?includeArchived=true' : ''}`,
      ),
  });

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'plans'] });
  }, [queryClient]);

  const createMutation = useMutation<PlanResponse, Error, PlanFormState>({
    mutationFn: (payload) =>
      api.post<PlanResponse>('/api/v1/admin/plans', {
        slug: payload.slug.trim().toLowerCase(),
        name: payload.name.trim(),
        monthlyPriceUsdCents: payload.monthlyPriceUsdCents,
        yearlyPriceUsdCents: payload.yearlyPriceUsdCents ?? undefined,
        trialDays: payload.trialDays,
        stripePriceId: payload.stripePriceId.trim() || null,
        shopifyAppPlanHandle: payload.shopifyAppPlanHandle.trim() || null,
        discountPercentage: Number(payload.discountPercentage) || 0,
        storageLimit: payload.storageLimit,
        exportsLimit: payload.exportsLimit,
        storesLimit: payload.storesLimit,
        usersLimit: payload.usersLimit,
        features: payload.features,
        sortOrder: payload.sortOrder,
        isActive: payload.isActive,
      }),
    onSuccess: () => {
      invalidate();
      setModalMode(null);
      setActionMessage('Plan created.');
    },
    onError: (err) => setFormError(err.message),
  });

  const updateMutation = useMutation<
    PlanResponse,
    Error,
    { id: string; payload: PlanFormState }
  >({
    mutationFn: ({ id, payload }) =>
      api.patch<PlanResponse>(`/api/v1/admin/plans/${id}`, {
        name: payload.name.trim(),
        monthlyPriceUsdCents: payload.monthlyPriceUsdCents,
        yearlyPriceUsdCents: payload.yearlyPriceUsdCents ?? null,
        trialDays: payload.trialDays,
        stripePriceId: payload.stripePriceId.trim() || null,
        shopifyAppPlanHandle: payload.shopifyAppPlanHandle.trim() || null,
        discountPercentage: Number(payload.discountPercentage) || 0,
        storageLimit: payload.storageLimit,
        exportsLimit: payload.exportsLimit,
        storesLimit: payload.storesLimit,
        usersLimit: payload.usersLimit,
        features: payload.features,
        sortOrder: payload.sortOrder,
        isActive: payload.isActive,
      }),
    onSuccess: () => {
      invalidate();
      setModalMode(null);
      setActionMessage('Plan updated.');
    },
    onError: (err) => setFormError(err.message),
  });

  const lifecycleMutation = useMutation<
    PlanResponse,
    Error,
    {
      id: string;
      action: 'archive' | 'restore' | 'deprecate' | 'undeprecate';
    }
  >({
    mutationFn: ({ id, action }) =>
      api.post<PlanResponse>(`/api/v1/admin/plans/${id}/${action}`, {}),
    onSuccess: (_data, vars) => {
      invalidate();
      setActionMessage(`Plan ${vars.action} succeeded.`);
    },
    onError: (err) => setActionMessage(err.message),
  });

  // ---------------------------------------------------------------------
  // Form helpers
  // ---------------------------------------------------------------------

  const openCreate = useCallback(() => {
    setEditTarget(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setModalMode('create');
  }, []);

  const openEdit = useCallback((plan: Plan) => {
    setEditTarget(plan);
    setForm(planToForm(plan));
    setFormError(null);
    setModalMode('edit');
  }, []);

  const closeModal = useCallback(() => {
    setModalMode(null);
    setEditTarget(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    createMutation.reset();
    updateMutation.reset();
  }, [createMutation, updateMutation]);

  const handleField = useCallback(
    <K extends keyof PlanFormState>(field: K, value: PlanFormState[K]) => {
      setForm((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  const handleCents = useCallback(
    (field: 'monthlyPriceUsdCents' | 'yearlyPriceUsdCents') =>
      (e: ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        if (field === 'yearlyPriceUsdCents' && raw.trim() === '') {
          setForm((prev) => ({ ...prev, yearlyPriceUsdCents: null }));
          return;
        }
        setForm((prev) => ({ ...prev, [field]: parseCents(raw) }));
      },
    [],
  );

  const handleInt = useCallback(
    (field: keyof PlanFormState) =>
      (e: ChangeEvent<HTMLInputElement>) => {
        const num =
          e.target.value === '' ? 0 : parseInt(e.target.value, 10);
        setForm((prev) => ({
          ...prev,
          [field]: Number.isNaN(num) ? 0 : num,
        }));
      },
    [],
  );

  const handleToggleFeature = useCallback((feature: string) => {
    setForm((prev) => ({
      ...prev,
      features: prev.features.includes(feature)
        ? prev.features.filter((f) => f !== feature)
        : [...prev.features, feature],
    }));
  }, []);

  // ---------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------

  const handleSubmit = useCallback(() => {
    setFormError(null);
    const scope = deriveProviderScope(form);
    if (form.slug !== 'free' && scope === 'INTERNAL') {
      setFormError(
        'Paid plans must set at least one billing handle: Stripe Price id and/or Shopify plan handle.',
      );
      return;
    }
    if (modalMode === 'create') {
      if (!form.slug.trim()) {
        setFormError('Slug is required.');
        return;
      }
      if (!form.name.trim()) {
        setFormError('Name is required.');
        return;
      }
      createMutation.mutate(form);
    } else if (modalMode === 'edit' && editTarget) {
      if (
        !form.isActive &&
        (editTarget.organizationCount > 0 ||
          editTarget.activeSubscriptionCount > 0)
      ) {
        setFormError(
          `Cannot deactivate — ${editTarget.organizationCount} org(s) and ${editTarget.activeSubscriptionCount} open subscription(s) still reference this plan. Use "Deprecate" instead.`,
        );
        return;
      }
      updateMutation.mutate({ id: editTarget.id, payload: form });
    }
  }, [createMutation, editTarget, form, modalMode, updateMutation]);

  // ---------------------------------------------------------------------
  // Derived
  // ---------------------------------------------------------------------

  const plans = plansQuery.data?.data ?? [];
  const formScope = useMemo(() => deriveProviderScope(form), [form]);

  // Auto-clear action banner after 5s
  useEffect(() => {
    if (!actionMessage) return;
    const t = setTimeout(() => setActionMessage(null), 5000);
    return () => clearTimeout(t);
  }, [actionMessage]);

  // ---------------------------------------------------------------------
  // Loading / error
  // ---------------------------------------------------------------------

  if (plansQuery.isLoading && !plansQuery.data) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size="lg" label="Loading plans..." />
      </div>
    );
  }

  if (plansQuery.isError) {
    return (
      <Card>
        <div className="flex flex-col items-center justify-center gap-4 py-16">
          <p className="text-sm text-danger">
            Failed to load plans: {plansQuery.error.message}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void plansQuery.refetch()}
          >
            Retry
          </Button>
        </div>
      </Card>
    );
  }

  // ---------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------

  return (
    <div className="space-y-6">
      {actionMessage && (
        <div className="rounded-md bg-success/10 px-4 py-3 text-sm text-success">
          {actionMessage}
        </div>
      )}

      <Card>
        <CardHeader
          title="Plan Management"
          description="Manage subscription plans, billing handles, lifecycle, and limits. Plans are FK-protected; archive (soft-delete) is the terminal state."
          action={
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-text-secondary">
                <input
                  type="checkbox"
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                />
                Include archived
              </label>
              <Button onClick={openCreate}>New Plan</Button>
            </div>
          }
        />

        <div className="mt-4 overflow-x-auto rounded-lg border border-border">
          {plans.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-sm text-text-tertiary">
              No plans found.
            </div>
          ) : (
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-surface-secondary">
                <tr>
                  <Th>Plan</Th>
                  <Th>Provider scope</Th>
                  <Th>Monthly</Th>
                  <Th>Discount</Th>
                  <Th>Limits</Th>
                  <Th>Usage</Th>
                  <Th>Status</Th>
                  <Th className="text-right">Actions</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-surface">
                {plans.map((plan) => (
                  <PlanRow
                    key={plan.id}
                    plan={plan}
                    onEdit={openEdit}
                    onLifecycle={(action) =>
                      lifecycleMutation.mutate({ id: plan.id, action })
                    }
                    lifecyclePending={lifecycleMutation.isPending}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>

      <Modal
        open={modalMode !== null}
        onClose={closeModal}
        title={
          modalMode === 'create'
            ? 'New Plan'
            : editTarget
              ? `Edit Plan — ${editTarget.name}`
              : 'Edit Plan'
        }
        size="lg"
      >
        {modalMode !== null && (
          <div>
            {formError && (
              <div className="mb-4 rounded-md bg-danger/10 px-4 py-3 text-sm text-danger">
                {formError}
              </div>
            )}

            <div className="mb-4 rounded-md border border-border bg-surface-secondary px-4 py-3 text-sm">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-text-primary">
                  Provider scope:
                </span>
                <ProviderScopeBadge scope={formScope} />
              </div>
              <p className="mt-1 text-xs text-text-tertiary">
                {describeProviderScope(formScope)}
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                label="Plan Name"
                value={form.name}
                onChange={(e) => handleField('name', e.target.value)}
              />

              {modalMode === 'create' ? (
                <Input
                  label="Slug"
                  value={form.slug}
                  onChange={(e) =>
                    handleField('slug', e.target.value.toLowerCase())
                  }
                  hint="Immutable after create. Lowercase letters, digits, hyphens (e.g. pro, pro-annual)."
                />
              ) : (
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1.5">
                    Slug
                  </label>
                  <code className="block rounded-md border border-border bg-surface-secondary px-3 py-2 text-sm text-text-tertiary">
                    {form.slug}
                  </code>
                  <p className="mt-1 text-xs text-text-tertiary">
                    Slug is immutable after create.
                  </p>
                </div>
              )}

              <Input
                label="Stripe Price ID"
                value={form.stripePriceId}
                placeholder="price_..."
                onChange={(e) =>
                  handleField('stripePriceId', e.target.value)
                }
                hint="Leave empty for Shopify-only or free plans."
              />

              <Input
                label="Shopify Plan Handle"
                value={form.shopifyAppPlanHandle}
                onChange={(e) =>
                  handleField('shopifyAppPlanHandle', e.target.value)
                }
                hint='MUST match the plan name in Shopify Partner Dashboard "Managed Pricing".'
              />

              <Input
                label="Monthly Price (USD)"
                type="number"
                step="0.01"
                value={
                  form.monthlyPriceUsdCents === 0
                    ? '0'
                    : (form.monthlyPriceUsdCents / 100).toFixed(2)
                }
                onChange={handleCents('monthlyPriceUsdCents')}
              />

              <Input
                label="Yearly Price (USD)"
                type="number"
                step="0.01"
                value={
                  form.yearlyPriceUsdCents == null
                    ? ''
                    : (form.yearlyPriceUsdCents / 100).toFixed(2)
                }
                placeholder="(optional)"
                onChange={handleCents('yearlyPriceUsdCents')}
              />

              <Input
                label="Trial days"
                type="number"
                value={String(form.trialDays)}
                onChange={handleInt('trialDays')}
                hint="0 disables trial."
              />

              <Input
                label="Sort order"
                type="number"
                value={String(form.sortOrder)}
                onChange={handleInt('sortOrder')}
                hint="Lower values appear first in the catalog."
              />

              <Input
                label="Discount %"
                value={form.discountPercentage}
                onChange={(e) =>
                  handleField('discountPercentage', e.target.value)
                }
                hint="Reserved for the Phase 2 wholesale discount engine."
              />

              <Input
                label="Storage Limit (MB)"
                type="number"
                value={String(form.storageLimit)}
                onChange={handleInt('storageLimit')}
                hint="-1 for unlimited"
              />

              <Input
                label="Exports Limit"
                type="number"
                value={String(form.exportsLimit)}
                onChange={handleInt('exportsLimit')}
                hint="-1 for unlimited"
              />

              <Input
                label="Stores Limit"
                type="number"
                value={String(form.storesLimit)}
                onChange={handleInt('storesLimit')}
                hint="-1 for unlimited"
              />

              <Input
                label="Users Limit"
                type="number"
                value={String(form.usersLimit)}
                onChange={handleInt('usersLimit')}
                hint="-1 for unlimited"
              />

              <div className="flex items-center gap-3 sm:col-span-2">
                <button
                  type="button"
                  role="switch"
                  aria-checked={form.isActive}
                  onClick={() => handleField('isActive', !form.isActive)}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ${
                    form.isActive ? 'bg-brand-600' : 'bg-border'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition-transform duration-200 ${
                      form.isActive ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
                <span className="text-sm text-text-primary">
                  {form.isActive ? 'Active' : 'Inactive'}
                </span>
                {!form.isActive &&
                  editTarget &&
                  (editTarget.organizationCount > 0 ||
                    editTarget.activeSubscriptionCount > 0) && (
                    <span className="text-xs text-danger">
                      ({editTarget.organizationCount} org(s) +{' '}
                      {editTarget.activeSubscriptionCount} open sub(s) use this
                      plan — use Deprecate instead)
                    </span>
                  )}
              </div>
            </div>

            <div className="mt-6">
              <p className="text-sm font-medium text-text-primary mb-2">
                Features
              </p>
              <div className="flex flex-wrap gap-2">
                {AVAILABLE_FEATURES.map((feat) => {
                  const active = form.features.includes(feat.value);
                  return (
                    <button
                      key={feat.value}
                      type="button"
                      onClick={() => handleToggleFeature(feat.value)}
                      className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                        active
                          ? 'border-brand-600 bg-brand-50 text-brand-700'
                          : 'border-border bg-surface text-text-tertiary hover:border-brand-300 hover:text-text-primary'
                      }`}
                    >
                      {feat.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <ModalFooter>
              <Button variant="outline" onClick={closeModal}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                loading={createMutation.isPending || updateMutation.isPending}
              >
                {modalMode === 'create' ? 'Create plan' : 'Save changes'}
              </Button>
            </ModalFooter>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Th({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <th
      className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary ${className ?? ''}`}
    >
      {children}
    </th>
  );
}

function ProviderScopeBadge({ scope }: { scope: ProviderScope }) {
  if (scope === 'BOTH') {
    return (
      <Badge variant="info" size="sm">
        Stripe + Shopify
      </Badge>
    );
  }
  if (scope === 'STRIPE_ONLY') {
    return (
      <Badge variant="info" size="sm">
        Stripe only
      </Badge>
    );
  }
  if (scope === 'SHOPIFY_ONLY') {
    return (
      <Badge variant="success" size="sm">
        Shopify Managed
      </Badge>
    );
  }
  return (
    <Badge variant="default" size="sm">
      Internal
    </Badge>
  );
}

function PlanRow({
  plan,
  onEdit,
  onLifecycle,
  lifecyclePending,
}: {
  plan: Plan;
  onEdit: (plan: Plan) => void;
  onLifecycle: (
    action: 'archive' | 'restore' | 'deprecate' | 'undeprecate',
  ) => void;
  lifecyclePending: boolean;
}) {
  const archived = plan.archivedAt != null;
  const deprecated = plan.deprecatedAt != null;

  return (
    <tr className="transition-colors hover:bg-surface-secondary">
      <td className="px-4 py-3 whitespace-nowrap">
        <div>
          <p className="text-sm font-medium text-text-primary">{plan.name}</p>
          <p className="text-xs text-text-tertiary">
            <code>{plan.slug}</code>
          </p>
        </div>
      </td>

      <td className="px-4 py-3 whitespace-nowrap">
        <ProviderScopeBadge scope={plan.providerScope} />
      </td>

      <td className="px-4 py-3 text-sm text-text-primary whitespace-nowrap">
        {centsToDecimal(plan.monthlyPriceUsdCents)}
        {plan.trialDays > 0 && (
          <span className="ml-2 text-xs text-text-tertiary">
            ({plan.trialDays}d trial)
          </span>
        )}
      </td>

      <td className="px-4 py-3 text-sm whitespace-nowrap">
        {parseFloat(plan.discountPercentage) > 0 ? (
          <Badge variant="success" size="sm">
            {plan.discountPercentage}%
          </Badge>
        ) : (
          <span className="text-text-tertiary">—</span>
        )}
      </td>

      <td className="px-4 py-3 whitespace-nowrap">
        <div className="flex flex-wrap gap-1 text-xs text-text-secondary">
          <span title="Storage">{formatLimit(plan.storageLimit)} MB</span>
          <span className="text-text-tertiary">·</span>
          <span title="Exports">{formatLimit(plan.exportsLimit)} exp</span>
          <span className="text-text-tertiary">·</span>
          <span title="Stores">{formatLimit(plan.storesLimit)} stores</span>
          <span className="text-text-tertiary">·</span>
          <span title="Users">{formatLimit(plan.usersLimit)} users</span>
        </div>
      </td>

      <td className="px-4 py-3 text-sm text-text-secondary whitespace-nowrap text-center">
        <div>
          {plan.organizationCount} org{plan.organizationCount !== 1 ? 's' : ''}
        </div>
        <div className="text-xs text-text-tertiary">
          {plan.activeSubscriptionCount} sub
          {plan.activeSubscriptionCount !== 1 ? 's' : ''}
        </div>
      </td>

      <td className="px-4 py-3 whitespace-nowrap">
        <div className="flex flex-col gap-1">
          {archived ? (
            <Badge variant="default" size="sm" dot>
              Archived
            </Badge>
          ) : deprecated ? (
            <Badge variant="warning" size="sm" dot>
              Deprecated
            </Badge>
          ) : plan.isActive ? (
            <Badge variant="success" size="sm" dot>
              Active
            </Badge>
          ) : (
            <Badge variant="default" size="sm" dot>
              Inactive
            </Badge>
          )}
        </div>
      </td>

      <td className="px-4 py-3 whitespace-nowrap text-right">
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onEdit(plan)}
            disabled={archived}
          >
            Edit
          </Button>
          {archived ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onLifecycle('restore')}
              loading={lifecyclePending}
            >
              Restore
            </Button>
          ) : (
            <Fragment>
              {deprecated ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onLifecycle('undeprecate')}
                  loading={lifecyclePending}
                >
                  Re-list
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onLifecycle('deprecate')}
                  loading={lifecyclePending}
                >
                  Deprecate
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  if (
                    window.confirm(
                      `Archive "${plan.name}"? Existing organizations / subscriptions must already reference a different plan.`,
                    )
                  ) {
                    onLifecycle('archive');
                  }
                }}
                loading={lifecyclePending}
              >
                Archive
              </Button>
            </Fragment>
          )}
        </div>
      </td>
    </tr>
  );
}
